import { parseWidgetSettings } from '@teo/widget-shared';
import {
  bootstrapWidget,
  createHttpSendMessage,
  createHttpSendImageMessage,
  createMockSendMessage,
  createMockSendImageMessage,
  createModuleRegistry,
  configureWidgetAnalytics,
  flushWidgetAnalytics,
  getWidgetAnalyticsQueueSize,
  installHumanGate,
  installPageVisitAnalytics,
  installWidgetAnalyticsLifecycle,
  startCartWatchers,
  isTeoAiAgentVisit,
  isWithinSchedule,
  parseScriptConfig,
  reportWidgetEvent,
  reportWidgetError,
  resolveReverbConfig,
  setImageDebugEnabled,
  setPopupDebugEnabled,
  setRealtimeDebugEnabled,
  setScrollDebugEnabled,
  setSyncDebugEnabled,
  WidgetModulesProvider,
  type ScriptTagConfig,
} from '@teo/widget-core';
import { mountInShadow, WidgetShell } from '@teo/widget-ui';
import { WidgetErrorBoundary } from './WidgetErrorBoundary.js';

const INIT_ATTR = 'data-teo-initialized';

/** Prod embeds wait this long before any auth/analytics/mount (anti short-lived parsers). */
export const PRODUCTION_BOOT_DELAY_MS = 3000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export type TeoWidgetInstance = {
  host: HTMLElement;
  destroy: () => void;
};

function createHostElement(slug: string | null): HTMLElement {
  const host = document.createElement('div');
  host.id = slug ? `teo-widget-host-${slug}` : 'teo-widget-host';
  host.setAttribute('data-teo-widget', '');
  document.body.appendChild(host);
  return host;
}

async function mountWidget(
  config: ScriptTagConfig,
): Promise<TeoWidgetInstance | null> {
  const { settings: raw, session } = await bootstrapWidget(config);
  const settings = parseWidgetSettings(raw);

  setRealtimeDebugEnabled(config.wsDebug);
  setImageDebugEnabled(config.imageDebug);
  setScrollDebugEnabled(config.scrollDebug);
  setSyncDebugEnabled(config.syncDebug);
  setPopupDebugEnabled(config.popupDebug);

  if (!settings.content.widgetVisible) {
    return null;
  }

  if (!isWithinSchedule(settings.content.messageSchedule)) {
    return null;
  }

  const registry = createModuleRegistry(settings);
  const host = createHostElement(config.slug);
  const apiBase = config.apiBase || window.location.origin;
  const sendMessage = config.mock
    ? createMockSendMessage()
    : createHttpSendMessage(apiBase);
  const sendImageMessage = config.mock
    ? createMockSendImageMessage()
    : createHttpSendImageMessage(apiBase);

  const reverbConfig = config.mock
    ? null
    : resolveReverbConfig({
        apiBase,
        key: config.reverbKey,
        host: config.reverbHost,
        port: config.reverbPort,
        scheme: config.reverbScheme ?? 'https',
      });

  const destroy = mountInShadow(host, () => (
    <WidgetErrorBoundary
      onError={(error, info) =>
        reportWidgetError(error, {
          source: 'render',
          fatal: true,
          componentStack: info.componentStack,
          slug: config.slug,
        })
      }
    >
      <WidgetModulesProvider registry={registry}>
        <WidgetShell
          rawSettings={raw}
          threadId={session.threadId}
          sendMessage={sendMessage}
          sendImageMessage={sendImageMessage}
          apiBase={apiBase}
          reverbConfig={reverbConfig}
          mock={config.mock}
          popupDebug={config.popupDebug}
        />
      </WidgetModulesProvider>
    </WidgetErrorBoundary>
  ));

  reportWidgetEvent('widget_loaded', { mock: config.mock });

  return { host, destroy };
}

/** Bootstrap a single loader script tag. Idempotent per element. */
export async function bootstrapFromScript(
  script: HTMLScriptElement,
): Promise<TeoWidgetInstance | null> {
  if (script.hasAttribute(INIT_ATTR)) return null;
  script.setAttribute(INIT_ATTR, 'true');

  // Teo AI agent crawl: do not mount, login, greet, or emit visit analytics.
  if (isTeoAiAgentVisit()) {
    return null;
  }

  const config = parseScriptConfig(script);

  if (!config.mock && !config.slug) {
    console.error('[Teo Widget] Missing data-slug on loader script');
    return null;
  }

  // Prod only: wait before any network / listeners so short-lived parsers leave
  // without login, settings, analytics, or mount. Mock skips for playground.
  if (!config.mock) {
    await sleep(PRODUCTION_BOOT_DELAY_MS);
  }

  // Configure before login/settings so bootstrap failures can use the same
  // /api/widget/events pipeline. The config is refreshed again after bootstrap.
  configureWidgetAnalytics({
    apiBase: config.apiBase || null,
    backendEnabled: !config.mock && config.analyticsBackend,
    debug: config.analyticsDebug,
    // Keep events off the page promptly — without this, chat/popup events stay
    // in memory until tab hide / greeting prefetch flush.
    flushDebounceMs: 3000,
  });
  installWidgetAnalyticsLifecycle();
  // Soft human-gate before page visits / greeting consumers mount.
  installHumanGate({ enabled: config.humanGate });
  // Before React mounts behavior watchers so SPA navigate snapshots run first.
  installPageVisitAnalytics();
  // Cart aggregates for analytics (independent of CONTEXT_ENABLED).
  startCartWatchers();

  try {
    return await mountWidget(config);
  } catch (error) {
    console.error('[Teo Widget] Failed to initialize', error);
    reportWidgetError(error, {
      source: 'bootstrap',
      fatal: true,
      slug: config.slug,
    });
    return null;
  }
}

/** Find all loader scripts on the page and bootstrap them. */
export function autoBootstrap(): void {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    `script[data-slug]:not([${INIT_ATTR}]), script[data-mock]:not([${INIT_ATTR}])`,
  );

  for (const script of scripts) {
    void bootstrapFromScript(script);
  }
}

export type TeoWidgetGlobal = {
  bootstrapFromScript: typeof bootstrapFromScript;
  autoBootstrap: typeof autoBootstrap;
  /** Send queued analytics batch to `POST /api/widget/events` now. */
  flushAnalytics: typeof flushWidgetAnalytics;
  getAnalyticsQueueSize: typeof getWidgetAnalyticsQueueSize;
};

declare global {
  interface Window {
    TeoWidget?: TeoWidgetGlobal;
  }
}

export function exposeGlobalApi(): void {
  window.TeoWidget = {
    bootstrapFromScript,
    autoBootstrap,
    flushAnalytics: flushWidgetAnalytics,
    getAnalyticsQueueSize: getWidgetAnalyticsQueueSize,
  };
}
